import groupBy from 'lodash/groupBy'
import moment from 'moment'

import { Collection } from 'modules/core/lib/mobx-rest'

import { ResultConceptsType } from 'modules/payroll_bundle/lib/constants'
import Payment, { PAID } from 'modules/payroll_bundle/store/models/Payment'
import type PaymentItem from 'modules/payroll_bundle/store/models/PaymentItem'

export class Payments extends Collection<Payment> {
  model() {
    return Payment
  }

  currentPeriodPayments(
    policyPeriodId: number | null,
    type?: ResultConceptsType | undefined | null
  ): Payment[] {
    if (!policyPeriodId) return []
    if (type) {
      return this.filter({ policy_period_id: policyPeriodId, concept: type })
    } else {
      return this.filter({ policy_period_id: policyPeriodId })
    }
  }

  periodPaymentsGroupedByConcept(payments: Payment[]) {
    return groupBy(payments, 'concept')
  }

  static sortPayments(payments: Payment[]): Payment[] {
    return payments.sort(
      (a, b) =>
        moment(a.attributes.created_at).valueOf() - moment(b.attributes.created_at).valueOf()
    )
  }

  getByConcept(policyPeriodId: number, concept: ResultConceptsType): Payment[] {
    return this.filter({ policy_period_id: policyPeriodId, concept })
  }

  getPolicyPeriodTotals(policyPeriodId: number): {
    totalPaidAmount: number
    totalPendingAmount: number
    numberOfEmployeesPaid: number
    numberOfEmployeesPendingToPay: number
  } {
    const payments = this.currentPeriodPayments(policyPeriodId)

    const { totalPaidAmount, totalPendingAmount, employeesPaid, employeesToBePaid } =
      payments.reduce(
        (totals, payment) => {
          const paymentItems = payment.paymentItems

          if (payment.attributes.status === PAID) {
            return {
              ...totals,
              employeesPaid: new Set([
                ...totals.employeesPaid,
                ...paymentItems.map((paymentItem) => paymentItem.attributes.employee_id),
              ]),
              totalPaidAmount: totals.totalPaidAmount + payment.paymentsTotalAmount,
            }
          }
          return {
            ...totals,
            employeesToBePaid: new Set([
              ...totals.employeesToBePaid,
              ...paymentItems.map((paymentItem) => paymentItem.attributes.employee_id),
            ]),
            totalPendingAmount: totals.totalPendingAmount + payment.paymentsTotalAmount,
          }
        },
        {
          totalPaidAmount: 0,
          totalPendingAmount: 0,
          employeesPaid: new Set(),
          employeesToBePaid: new Set(),
        }
      )

    return {
      totalPaidAmount,
      totalPendingAmount,
      numberOfEmployeesPaid: employeesPaid.size,
      numberOfEmployeesPendingToPay: employeesToBePaid.size,
    }
  }
}

const singleton = new Payments()

export const paymentItemConcept = (
  paymentItem: PaymentItem
): ResultConceptsType | null | undefined => {
  if (!paymentItem.has('payment_id')) return null

  return singleton.get(paymentItem.attributes.payment_id)?.attributes.concept
}

export default singleton
